//@ts-nocheck
import {ScrollView} from 'react-native-gesture-handler';
import {Platform, StyleSheet, View} from 'react-native';
import React, {useRef, useState, useEffect} from 'react';
import {Theme} from '../theme/lightTheme';
import {getFontFamily, openUrl} from './helpers';
import Text from '../components/textGeneric';
import {wdp, isWeb} from './responsive';

interface ParsedTextPart {
  text: string;
  isHtml: boolean;
  styles?: {color?: string; fontWeight?: string; fontStyle?: string};
}

/**
 * Parses text that may contain inline HTML tags and returns an array of parts
 * Each part indicates whether it's plain text or styled HTML content
 */
const parseTextWithHtml = (content: string): ParsedTextPart[] => {
  const parts: ParsedTextPart[] = [];

  // Regex to match HTML span tags with style, or simple tags like <b>, <i>, <strong>, <em>
  const htmlRegex =
    /<span\s+style=["']([^"']*)["']>([\s\S]*?)<\/span>|<(b|strong|i|em|u)>([\s\S]*?)<\/\3>/gi;

  let lastIndex = 0;
  let match;

  while ((match = htmlRegex.exec(content)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push({
        text: content.slice(lastIndex, match.index),
        isHtml: false,
      });
    }

    if (match[1] !== undefined) {
      // It's a <span style="...">...</span>
      const styleStr = match[1];
      const innerText = match[2];
      const styles: {color?: string; fontWeight?: string; fontStyle?: string} =
        {};

      // Parse color
      const colorMatch = styleStr.match(/color\s*:\s*([^;]+)/i);
      if (colorMatch) {
        styles.color = colorMatch[1].trim();
      }

      // Parse font-weight
      const weightMatch = styleStr.match(/font-weight\s*:\s*([^;]+)/i);
      if (weightMatch) {
        styles.fontWeight = weightMatch[1].trim();
      }

      // Parse font-style
      const fontStyleMatch = styleStr.match(/font-style\s*:\s*([^;]+)/i);
      if (fontStyleMatch) {
        styles.fontStyle = fontStyleMatch[1].trim();
      }

      parts.push({
        text: innerText,
        isHtml: true,
        styles,
      });
    } else if (match[3] !== undefined) {
      // It's a simple tag like <b>, <i>, <strong>, <em>, <u>
      const tag = match[3].toLowerCase();
      const innerText = match[4];
      const styles: {color?: string; fontWeight?: string; fontStyle?: string} =
        {};

      if (tag === 'b' || tag === 'strong') {
        styles.fontWeight = 'bold';
      } else if (tag === 'i' || tag === 'em') {
        styles.fontStyle = 'italic';
      }

      parts.push({
        text: innerText,
        isHtml: true,
        styles,
      });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last match
  if (lastIndex < content.length) {
    parts.push({
      text: content.slice(lastIndex),
      isHtml: false,
    });
  }

  // If no HTML was found, return the whole content as plain text
  if (parts.length === 0) {
    parts.push({text: content, isHtml: false});
  }

  return parts;
};

/**
 * Parses inline HTML and extracts style information
 * Supports: <span style="color:red">text</span>, <b>text</b>, <i>text</i>, etc.
 */
const parseInlineHtml = (
  htmlContent: string,
): {text: string; styles: {color?: string; fontWeight?: string; fontStyle?: string}} => {
  const styles: {color?: string; fontWeight?: string; fontStyle?: string} = {};
  let text = htmlContent;

  // Extract content from span tags with style
  const spanMatch = htmlContent.match(
    /<span\s+style=["']([^"']*)["']>([\s\S]*?)<\/span>/i,
  );
  if (spanMatch) {
    const styleStr = spanMatch[1];
    text = spanMatch[2];

    // Parse color from style
    const colorMatch = styleStr.match(/color\s*:\s*([^;]+)/i);
    if (colorMatch) {
      styles.color = colorMatch[1].trim();
    }

    // Parse font-weight from style
    const weightMatch = styleStr.match(/font-weight\s*:\s*([^;]+)/i);
    if (weightMatch) {
      styles.fontWeight = weightMatch[1].trim();
    }

    // Parse font-style from style
    const fontStyleMatch = styleStr.match(/font-style\s*:\s*([^;]+)/i);
    if (fontStyleMatch) {
      styles.fontStyle = fontStyleMatch[1].trim();
    }
  }

  // Handle <b> or <strong> tags
  const boldMatch = text.match(/<(?:b|strong)>([\s\S]*?)<\/(?:b|strong)>/i);
  if (boldMatch) {
    text = boldMatch[1];
    styles.fontWeight = 'bold';
  }

  // Handle <i> or <em> tags
  const italicMatch = text.match(/<(?:i|em)>([\s\S]*?)<\/(?:i|em)>/i);
  if (italicMatch) {
    text = italicMatch[1];
    styles.fontStyle = 'italic';
  }

  // Handle <u> tags (underline - we'll use borderBottom for native)
  const underlineMatch = text.match(/<u>([\s\S]*?)<\/u>/i);
  if (underlineMatch) {
    text = underlineMatch[1];
  }

  // Strip any remaining HTML tags
  text = text.replace(/<[^>]*>/g, '');

  return {text, styles};
};

// Custom scrollbar component for web
const CustomScrollbar: React.FC<{
  scrollRef: React.RefObject<HTMLDivElement>;
}> = ({scrollRef}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [scrollRatio, setScrollRatio] = useState(0);
  const [thumbWidth, setThumbWidth] = useState(0);
  const [showScrollbar, setShowScrollbar] = useState(false);
  const scrollbarRef = useRef<HTMLDivElement>(null);

  const updateScrollbar = () => {
    if (!scrollRef.current) return;

    const {scrollLeft, scrollWidth, clientWidth} = scrollRef.current;
    const hasOverflow = scrollWidth > clientWidth;
    setShowScrollbar(hasOverflow);

    if (hasOverflow) {
      const ratio = clientWidth / scrollWidth;
      const thumbW = Math.max(50, clientWidth * ratio);
      setThumbWidth(thumbW);
      setScrollRatio(scrollLeft / (scrollWidth - clientWidth));
    }
  };

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    updateScrollbar();
    scrollElement.addEventListener('scroll', updateScrollbar);
    window.addEventListener('resize', updateScrollbar);

    // Small delay to ensure table is rendered
    const timer = setTimeout(updateScrollbar, 100);

    return () => {
      clearTimeout(timer);
      scrollElement.removeEventListener('scroll', updateScrollbar);
      window.removeEventListener('resize', updateScrollbar);
    };
  }, [scrollRef]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !scrollRef.current || !scrollbarRef.current) return;

    const scrollbarRect = scrollbarRef.current.getBoundingClientRect();
    const scrollElement = scrollRef.current;
    const {scrollWidth, clientWidth} = scrollElement;

    const x = e.clientX - scrollbarRect.left;
    const maxThumbPosition = scrollbarRect.width - thumbWidth;
    const thumbPosition = Math.max(0, Math.min(x - thumbWidth / 2, maxThumbPosition));
    const scrollPosition = (thumbPosition / maxThumbPosition) * (scrollWidth - clientWidth);

    scrollElement.scrollLeft = scrollPosition;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, thumbWidth]);

  if (!showScrollbar) return null;

  const thumbPosition = scrollRatio * ((scrollbarRef.current?.clientWidth || 0) - thumbWidth);

  const getThumbColor = () => {
    if (isDragging) return '#333';
    if (isHovering) return '#555';
    return '#888';
  };

  return (
    <View
      ref={scrollbarRef}
      style={{
        width: '100%',
        height: 16,
        backgroundColor: '#f1f1f1',
        borderRadius: 8,
        marginTop: 8,
        position: 'relative',
        cursor: 'pointer',
        border: '1px solid #e0e0e0',
      }}>
      <View
        onMouseDown={handleMouseDown}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        style={{
          position: 'absolute',
          left: thumbPosition,
          top: 2,
          height: 12,
          width: thumbWidth,
          backgroundColor: getThumbColor(),
          borderRadius: 6,
          cursor: isDragging ? 'grabbing' : 'grab',
          transition: isDragging ? 'none' : 'background-color 0.2s',
        }}
      />
    </View>
  );
};

// Table wrapper component for responsive tables with scrollbars
const TableWrapper: React.FC<{children: React.ReactNode; nodeKey: string}> = ({
  children,
  nodeKey,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  if (isWeb) {
    return (
      <View key={nodeKey} style={{width: '100%', marginVertical: '4%'}}>
        <View
          ref={scrollRef}
          // @ts-ignore - className is a web prop
          className="markdown-table-scroll"
          style={{
            width: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            // @ts-ignore - web-specific style
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}>
          <View
            style={{
              flexDirection: 'column',
              minWidth: '100%',
            }}>
            {children}
          </View>
        </View>
        <CustomScrollbar scrollRef={scrollRef} />
      </View>
    );
  }

  return (
    <ScrollView
      nestedScrollEnabled
      key={nodeKey}
      horizontal
      showsHorizontalScrollIndicator
      keyboardShouldPersistTaps="handled"
      style={{width: '100%', marginVertical: '4%'}}>
      <View
        onStartShouldSetResponder={() => true}
        style={{flexDirection: 'column', width: '100%'}}>
        {children}
      </View>
    </ScrollView>
  );
};

export const markdownRules = (theme: Theme) => {
  return {
    table: (node: any, children: any) => {
      if (isWeb) {
        return (
          <View
            key={node.key}
            // @ts-ignore - className is a web prop
            className="markdown-table-scroll"
            style={{
              width: '100%',
              marginVertical: '4%',
              overflowX: 'auto',
              overflowY: 'hidden',
              // @ts-ignore - web-specific style
              WebkitOverflowScrolling: 'touch',
            }}>
            <table
              style={{
                borderCollapse: 'collapse',
                width: '100%',
                fontSize: theme.text.s11,
              }}>
              {children}
            </table>
          </View>
        );
      }
      return <TableWrapper nodeKey={node.key}>{children}</TableWrapper>;
    },
    thead: (node: any, children: any) => {
      if (isWeb) {
        return <thead key={node.key}>{children}</thead>;
      }
      return (
        <View
          key={node.key}
          style={{
            flexDirection: 'row',
            width: '100%',
            flexWrap: 'wrap',
          }}>
          {children}
        </View>
      );
    },
    tbody: (node: any, children: any) => {
      if (isWeb) {
        return <tbody key={node.key}>{children}</tbody>;
      }
      return (
        <View
          key={node.key}
          style={{
            flexDirection: 'column',
            width: '100%',
          }}>
          {children}
        </View>
      );
    },
    tr: (node: any, children: any) => {
      if (isWeb) {
        return <tr key={node.key}>{children}</tr>;
      }
      return (
        <View
          key={node.key}
          style={{
            flexDirection: 'row',
            width: '100%',
            flexWrap: 'wrap',
          }}>
          {children}
        </View>
      );
    },
    th: (node: any, children: any) => {
      if (isWeb) {
        return (
          <th
            key={node.key}
            style={{
              padding: '10px 8px',
              backgroundColor: theme.secondary + '77',
              border: `1px solid ${theme.text.light}AA`,
              textAlign: 'center',
              color: theme.text.light,
              fontWeight: 'bold',
              fontSize: theme.text.s11,
              whiteSpace: 'normal',
              wordBreak: 'keep-all',
            }}>
            {children}
          </th>
        );
      }
      return (
        <View
          key={node.key}
          style={{
            flex: 1,
            paddingVertical: 10,
            paddingHorizontal: 8,
            backgroundColor: theme.secondary + '77',
            borderWidth: 1,
            borderColor: theme.text.light + 'AA',
            justifyContent: 'center',
            alignItems: 'center',
            flexShrink: 0,
          }}>
          <Text
            textBreakStrategy="highQuality"
            adjustsFontSizeToFit
            numberOfLines={3}
            style={{
              color: theme.text.light,
              fontWeight: 'bold',
              textAlign: 'center',
              fontSize: theme.text.s11,
            }}>
            {children}
          </Text>
        </View>
      );
    },
    td: (node: any, children: any, parent, styles) => {
      if (isWeb) {
        return (
          <td
            key={node.key}
            style={{
              padding: '8px',
              border: `1px solid ${theme.tertiary}`,
              textAlign: 'center',
              fontSize: theme.text.s11,
              whiteSpace: 'normal',
              wordBreak: 'keep-all',
            }}>
            {children}
          </td>
        );
      }
      return (
        <View
          key={node.key}
          style={[
            {
              flex: 1,
              borderWidth: 1,
              borderColor: theme.tertiary,
              paddingVertical: 8,
              paddingHorizontal: 8,
              flexShrink: 0,
            },
            styles._VIEW_SAFE_td,
          ]}>
          <Text
            textBreakStrategy="highQuality"
            adjustsFontSizeToFit
            numberOfLines={5}
            style={{
              textAlign: 'center',
              fontSize: theme.text.s11,
            }}>
            {children}
          </Text>
        </View>
      );
    },
    heading1: (node, children, parent, styles) => (
      <Text
        bold
        key={node.key}
        style={{
          ...styles.heading,
          ...styles.heading1,
        }}>
        {children}
      </Text>
    ),
    heading2: (node, children, parent, styles) => (
      <Text
        bold
        key={node.key}
        style={{
          ...styles.heading,
          ...styles.heading2,
        }}>
        {children}
      </Text>
    ),
    heading3: (node, children, parent, styles) => (
      <Text
        bold
        key={node.key}
        style={{
          ...styles.heading,
          ...styles.heading3,
        }}>
        {children}
      </Text>
    ),
    heading4: (node, children, parent, styles) => (
      <Text
        bold
        key={node.key}
        style={{
          ...styles.heading,
          ...styles.heading4,
        }}>
        {children}
      </Text>
    ),
    heading5: (node, children, parent, styles) => (
      <Text
        bold
        key={node.key}
        style={{
          ...styles.heading,
          ...styles.heading5,
        }}>
        {children}
      </Text>
    ),
    heading6: (node, children, parent, styles) => (
      <Text
        bold
        key={node.key}
        style={{
          ...styles.heading,
          ...styles.heading6,
        }}>
        {children}
      </Text>
    ),
    hr: (node, children, parent, styles) => (
      <View key={node.key} style={styles.hr} />
    ),
    blockquote: (node, children, parent, styles) => (
      <View key={node.key} style={styles.blockquote}>
        {children}
      </View>
    ),
    bullet_list: (node, children, parent, styles) => (
      <View key={node.key} style={styles.list}>
        {children}
      </View>
    ),
    ordered_list: (node, children, parent, styles) => (
      <View key={node.key} style={styles.list}>
        {children}
      </View>
    ),
    list_item: (node, children, parent, styles) => {
      const isOrdered = parent.type === 'ordered_list';
      const bullet = isOrdered ? (
        <Text style={styles.listItemNumber}>{node.index + 1}. </Text>
      ) : (
        <Text style={styles.listItemBullet}>• </Text>
      );

      return (
        <View key={node.key} style={styles.listItem}>
          {bullet}
          <View style={{flex: 1}}>{children}</View>
        </View>
      );
    },
    fence: (node, children, parent, styles) => (
      <ScrollView
        horizontal
        nestedScrollEnabled
        style={styles.fence}>
        <Text key={node.key} style={styles.code}>
          {node.content}
        </Text>
      </ScrollView>
    ),
    code_inline: (node, children, parent, styles) => (
      <Text key={node.key} style={[styles.code, styles.code_inline]}>
        {node.content}
      </Text>
    ),
    text: (node, children, parent, styles, inheritedStyles = {}) => {
      const content = node.content || '';

      // Check if content contains HTML tags
      if (/<[^>]+>/.test(content)) {
        // Parse and render HTML-containing text
        const parts = parseTextWithHtml(content);

        if (isWeb) {
          return (
            <span key={node.key} style={inheritedStyles}>
              {parts.map((part, idx) =>
                part.isHtml ? (
                  <span
                    key={`${node.key}-${idx}`}
                    style={{
                      color: part.styles?.color,
                      fontWeight: part.styles?.fontWeight,
                      fontStyle: part.styles?.fontStyle,
                    }}>
                    {part.text}
                  </span>
                ) : (
                  <span key={`${node.key}-${idx}`}>{part.text}</span>
                ),
              )}
            </span>
          );
        }

        return (
          <Text key={node.key} style={[inheritedStyles, styles.text]}>
            {parts.map((part, idx) =>
              part.isHtml ? (
                <Text
                  key={`${node.key}-${idx}`}
                  style={{
                    color: part.styles?.color,
                    fontWeight: part.styles?.fontWeight as any,
                    fontStyle: part.styles?.fontStyle as any,
                  }}>
                  {part.text}
                </Text>
              ) : (
                <Text key={`${node.key}-${idx}`}>{part.text}</Text>
              ),
            )}
          </Text>
        );
      }

      return (
        <Text key={node.key} style={[inheritedStyles, styles.text]}>
          {content}
        </Text>
      );
    },
    textgroup: (node, children, parent, styles) => (
      <Text
        key={node.key}
        style={styles.textgroup}
        semiBold={(styles?.fontSize || 0) > theme.text.s6}>
        {children}
      </Text>
    ),
    paragraph: (node, children, parent, styles) => (
      <View key={node.key} style={styles.paragraph}>
        {children}
      </View>
    ),
    hardbreak: (node, children, parent, styles) => (
      <Text
        key={node.key}
        style={styles.hardbreak}
        semiBold={(styles?.fontSize || 0) > theme.text.s6}>
        {'\n'}
      </Text>
    ),
    softbreak: (node, children, parent, styles) => (
      <Text
        key={node.key}
        style={styles.softbreak}
        semiBold={(styles?.fontSize || 0) > theme.text.s6}>
        {'\n'}
      </Text>
    ),
    inline: (node, children, parent, styles) => (
      <Text
        key={node.key}
        style={styles.inline}
        semiBold={(styles?.fontSize || 0) > theme.text.s6}>
        {children}
      </Text>
    ),
    span: (node, children, parent, styles) => (
      <Text
        key={node.key}
        style={styles.span}
        semiBold={(styles?.fontSize || 0) > theme.text.s6}>
        {children}
      </Text>
    ),
    strong: (node, children, parent, styles) => (
      <Text bold key={node.key} style={styles.strong}>
        {children}
      </Text>
    ),
    em: (node, children, parent, styles) => (
      <Text key={node.key} style={styles.em}>
        {children}
      </Text>
    ),
    s: (node, children, parent, styles) => (
      <Text key={node.key} style={styles.s}>
        {children}
      </Text>
    ),
    link: (node, children, parent, styles, onLinkPress) => (
      <Text
        key={node.key}
        onTouchStart={() => openUrl(node.attributes.href, onLinkPress)}
        style={[
          styles.link,
          {
            color: theme.primary,
            textDecorationLine: 'underline',
            flex: 1,
            textAlign: 'left',
          },
        ]}>
        {children}
      </Text>
    ),
    // Handle inline HTML tags like <span style="color:red">text</span>
    html_inline: (node, children, parent, styles) => {
      const content = node.content || '';
      const {text, styles: htmlStyles} = parseInlineHtml(content);

      if (isWeb) {
        // For web, we can use dangerouslySetInnerHTML or styled span
        return (
          <span
            key={node.key}
            style={{
              color: htmlStyles.color,
              fontWeight: htmlStyles.fontWeight,
              fontStyle: htmlStyles.fontStyle,
            }}>
            {text}
          </span>
        );
      }

      return (
        <Text
          key={node.key}
          style={[
            styles.text,
            {
              color: htmlStyles.color,
              fontWeight: htmlStyles.fontWeight as any,
              fontStyle: htmlStyles.fontStyle as any,
            },
          ]}>
          {text}
        </Text>
      );
    },
    // Handle block-level HTML
    html_block: (node, children, parent, styles) => {
      const content = node.content || '';
      const {text, styles: htmlStyles} = parseInlineHtml(content);

      if (isWeb) {
        return (
          <View key={node.key} style={styles.paragraph}>
            <span
              style={{
                color: htmlStyles.color,
                fontWeight: htmlStyles.fontWeight,
                fontStyle: htmlStyles.fontStyle,
              }}>
              {text}
            </span>
          </View>
        );
      }

      return (
        <View key={node.key} style={styles.paragraph}>
          <Text
            style={[
              styles.text,
              {
                color: htmlStyles.color,
                fontWeight: htmlStyles.fontWeight as any,
                fontStyle: htmlStyles.fontStyle as any,
              },
            ]}>
            {text}
          </Text>
        </View>
      );
    },
  };
};

export const markdownStyle = (theme: Theme): StyleSheet.NamedStyles<any> => {
  return {
    body: {
      fontSize: theme.text.s7 || theme.text.s7 + 2,
      zIndex: 2,
      color: theme.text.dark,
    },
    link: {
      color: theme.settings.icon,
      textDecorationLine: 'underline',
      wordBreak: 'break-all',
      overflowWrap: 'break-word',
      whiteSpace: 'normal',
      display: 'inline',
      flexShrink: 1,
    },
    hr: {
      height: 1,
      backgroundColor: theme.primary,
    },
    heading: {
      fontWeight: 'bold',
    },
    heading1: {fontSize: 32, marginBottom: 8, color: theme.text.dark},
    heading2: {fontSize: 26, marginBottom: 6, color: theme.text.dark},
    heading3: {fontSize: 22, marginVertical: 4, color: theme.text.dark},
    heading4: {fontSize: 20, marginVertical: 3, color: theme.text.dark},
    heading5: {fontSize: 18, marginVertical: 2, color: theme.text.dark},
    heading6: {fontSize: 16, marginVertical: 1, color: theme.text.dark},
    blockquote: {
      backgroundColor: theme.background,
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderLeftWidth: 4,
      borderLeftColor: theme.primary,
      marginVertical: 4,
    },
    list: {
      marginVertical: 4,
      paddingLeft: 16,
    },
    listItem: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: 6,
    },
    listItemBullet: {
      marginRight: 8,
      fontSize: 18,
      fontWeight: 'bold',
    },
    listItemNumber: {
      marginRight: 8,
      fontWeight: 'bold',
      lineHeight: 24,
      marginLeft: -8,
      marginTop: 2,
    },
    paragraph: {
      marginBottom: 6,
      flexWrap: 'wrap',
    },
    fence: {
      backgroundColor: theme.cardBackgroundEnd,
      marginVertical: 6,
      borderRadius: 8,
      padding: 12,
    },
    code_inline: {
      backgroundColor: theme.cardBackgroundEnd,
      paddingHorizontal: 4,
      paddingVertical: 2,
      borderRadius: 4,
    },
    table: {
      flexDirection: 'column',
      borderWidth: 1,
      borderColor: theme.text.light + 'AA',
    },
    td: {
      flex: 1,
      borderWidth: 1,
      borderColor: theme.text.light + 'AA',
      padding: 6,
      textAlign: 'center',
    },
    tr: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      borderWidth: 1,
      borderColor: theme.text.light + 'AA',
      textAlign: 'center',
    },
    th: {
      flex: 1,
      padding: 6,
      borderWidth: 1,
      borderColor: theme.text.light + 'AA',
      textAlign: 'center',
      backgroundColor: theme.secondary,
      color: theme.text.light,
      fontWeight: 'bold',
    },
    strong: {
      fontWeight: 'bold',
    },
    textgroup: {
      marginVertical: 4,
    },
    bullet_list_icon: {
      marginVertical: 4,
    },
  };
};

export const openMDLink = (link: string) => {
  openUrl(link);
  return false;
};

export const cleanAndFormatMarkdown = (raw: string): string => {
  if (typeof raw !== 'string') {
    return '';
  }

  let text = raw.replace(/\\n/g, '\n').replace(/\\"/g, '"');
  text = text.replace(/\r/g, '');

  text = text.replace(/\n\n/g, '[PARAGRAPH_BREAK]');
  text = text.replace(/\n/g, '  \n');
  text = text.replace(/\[PARAGRAPH_BREAK\]/g, '\n\n');

  if (text.startsWith('"') && text.endsWith('"')) {
    text = text.slice(1, -1);
  }

  return text.trim();
};

export const convertUrlsToMarkdown = (urls?: string[]) => {
  if (!urls || urls.length === 0) {
    return '';
  }

  const markdownList = urls
    .map((url, index) => `${index + 1}. ${url}`)
    .join('\n');
  return `\n\n**CITATIONS:**\n${markdownList}`;
};

